import { afterNextRender, Component, DestroyRef, effect, ElementRef, inject, input, output, signal } from '@angular/core';
import { DmLoadingIndicator } from '@digitale-menschen/component-library-angular';

/**
 * Sentinel for infinite scrolling. Sits at the end of a list and reports through
 * {@link loadNext} as soon as it comes close to the viewport; paging stays with the
 * calling parent.
 */
@Component({
  selector: 'lp-infinite-scroll-trigger',
  imports: [DmLoadingIndicator],
  templateUrl: './infinite-scroll-trigger.component.html',
  styleUrl: './infinite-scroll-trigger.component.scss',
})
export class InfiniteScrollTriggerComponent {
  /** Whether there are any further entries left to load at all. */
  public readonly enabled = input<boolean>(true);
  /** Whether a load is in flight; prevents parallel requests for the same page. */
  public readonly isLoading = input<boolean>(false);
  /** Distance from the viewport at which loading starts ahead of time. */
  public readonly rootMargin = input<string>('400px 0px');
  /** Scroll container to observe within. `null` observes the viewport. */
  public readonly scrollRoot = input<Element | null>(null);
  public readonly loadNext = output<void>();

  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  /** Whether the sentinel currently lies inside the viewport, extended by `rootMargin`. */
  private readonly isIntersecting = signal<boolean>(false);

  /**
   * Requests the next page as soon as the sentinel is visible and no request is in
   * flight. An effect, so that `enabled` and `isLoading` are re-evaluated too — an
   * IntersectionObserver only fires on real visibility changes.
   */
  private readonly requestNextPageEffect = effect(() => {
    if (this.isIntersecting() && this.enabled() && !this.isLoading()) {
      this.loadNext.emit();
    }
  });

  constructor() {
    afterNextRender(() => this.observeSentinel());
  }

  /** Starts observing; via `afterNextRender`, because IntersectionObserver only exists in the browser. */
  private observeSentinel(): void {
    const observer = new IntersectionObserver(([entry]) => this.isIntersecting.set(entry.isIntersecting), {
      root: this.scrollRoot(),
      rootMargin: this.rootMargin(),
      threshold: 0,
    });

    observer.observe(this.elementRef.nativeElement);
    this.destroyRef.onDestroy(() => observer.disconnect());
  }
}
